/**
 * The marketing site's own beacon: aggregate visit/view/click counters for
 * the operator console (web/src/admin/MarketingMetricsTab.tsx), nothing
 * else. This is deliberately not a `fetch` tucked into a component — it is
 * the one place that decides what leaves the browser, so the rule stays
 * enforced in one spot instead of trusted at every call site:
 *
 *  - No cookie is read, set, or required. `sessionStorage` is used only to
 *    remember "this tab already counted as a visit" — per-tab, dies with
 *    the tab, never transmitted, and not an identifier.
 *  - Nothing identifying is ever sent: no IP (the server never reads one
 *    off the request either — see marketing-metrics.js), no header, no
 *    fingerprint, no persistent ID. The body is exactly `{kind, path,
 *    label}`.
 *  - `navigator.doNotTrack` set means nothing is sent at all, full stop —
 *    not even the aggregate count.
 *
 * This is why Legal.tsx can keep saying "no tracking" without this feature
 * making that false: a bucket keyed on (day, kind, path, label) cannot be
 * read back into "what did this visitor do".
 */

const VISIT_KEY = 'hs.metrics.visited';

function doNotTrackOn(): boolean {
  // Three different places browsers have put this over the years.
  const nav = navigator as Navigator & { msDoNotTrack?: string };
  const win = window as Window & { doNotTrack?: string };
  const value = nav.doNotTrack ?? win.doNotTrack ?? nav.msDoNotTrack ?? '';
  return value === '1' || value === 'yes';
}

function send(kind: 'visit' | 'view' | 'click', path: string, label = ''): void {
  if (typeof navigator === 'undefined') return;
  try {
    if (doNotTrackOn()) return;
  } catch {
    // A browser that throws reading the flag gets the safe default: say
    // nothing, rather than risk sending on a visitor who asked not to be.
    return;
  }

  const body = JSON.stringify({ kind, path, label });
  try {
    if (navigator.sendBeacon) {
      navigator.sendBeacon('/api/public/metrics', new Blob([body], { type: 'application/json' }));
      return;
    }
  } catch {
    // Fall through to fetch below.
  }
  try {
    fetch('/api/public/metrics', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {
      // Fire-and-forget: a visitor's page must never know this failed.
    });
  } catch {
    /* never surface anything to a visitor over a counter */
  }
}

/** Once per page load/navigation — called from SiteChrome's `SitePage`. */
export function trackView(path: string): void {
  send('view', path);
}

/**
 * Once per browser tab, ever — gated by a `sessionStorage` flag so a
 * visitor who looks at five pages in one tab is one visit, not five.
 */
export function trackVisit(path: string): void {
  try {
    if (sessionStorage.getItem(VISIT_KEY)) return;
    sessionStorage.setItem(VISIT_KEY, '1');
  } catch {
    // Private mode / storage blocked: skip the visit count entirely rather
    // than risk counting it again on every page for lack of a flag to set.
    return;
  }
  send('visit', path);
}

/** A primary call-to-action was clicked. `label` is a short, fixed name
 * chosen by the call site (e.g. "hero_start") — never user-entered text. */
export function trackClick(label: string, path: string): void {
  send('click', path, label);
}
